import type { ReactNode } from 'react';

import {
  POSTINGS_V3_GET_DATA,
  type PostingsV3DataQuery,
  type PostingsV3DataQueryVariables,
} from '@revelio/data-access';

import { useFilterExpressions } from '../../filters/contexts/FilterExpressions';
import {
  useFilterContext,
  useIsTaxonomyResolved,
} from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../filters/types/filters';
import { hasPrimaryFilter } from '../../filters/utils/common';
import {
  getPostingsV3FiltersV2Variables,
  getPostingsV3Variables,
} from '../../filters/utils/getPostingsV3Variables';
import { usePageConfig } from '../PageConfigProvider/PageConfigProvider';
import {
  PAY_PAGE_IDS,
  POSTINGS_PAGE_IDS,
  RevelioPageId,
} from '../PageConfigProvider/types';
import type { PostingChartType } from '../PageConfigProvider/types/chart';
import type { PostingSecondaryDimension } from '../PageConfigProvider/types/dimension';
import type { PostingMetricValues } from '../PageConfigProvider/types/metric';
import {
  RequestedPostingsModeProvider,
  useDefaultPostingsMetricModeOverride,
} from './postings/postings-metric-mode';
import {
  type BreakdownSingleDataSet,
  type DataProviderChildren,
  DataProviderQueryStatus,
  type SingleDataSet,
  type TimeseriesDataSet,
} from './types';
import { convertPostingsV3Data } from './utils/convert-postings-v3-data';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';
import { useWeekRefs } from './utils/useDateRefs';

type PostingData = SingleDataSet | TimeseriesDataSet | BreakdownSingleDataSet;

const POSTING_COUNT_MODE_PAGE_IDS: ReadonlySet<RevelioPageId> = new Set([
  RevelioPageId.COMPANY_OVERVIEW,
  ...POSTINGS_PAGE_IDS,
  ...PAY_PAGE_IDS,
]);

/**
 * The page's postings_v3 variables: `filters_v2` on a page that filters with
 * expressions, `filters` everywhere else.
 */
const usePostingsV3Variables = (
  args: Parameters<typeof getPostingsV3Variables>[0]
): PostingsV3DataQueryVariables => {
  const usesFilterExpressions = useFilterExpressions();
  return usesFilterExpressions
    ? getPostingsV3FiltersV2Variables(args)
    : getPostingsV3Variables(args);
};

type PostingDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  secondaryDimension?: PostingSecondaryDimension;
  chartType: PostingChartType;
  metric: PostingMetricValues;
  isSnapshot?: boolean;
  exchangeRate?: number;
  children: DataProviderChildren<PostingData | undefined>;
};

export const PostingDataProvider = ({
  primaryDimension,
  secondaryDimension,
  metric,
  isSnapshot = false,
  exchangeRate,
  children,
}: PostingDataProviderProps) => {
  const { activeFilterMap } = useFilterContext();
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'week' });
  const isPrimaryFilterSet = hasPrimaryFilter({
    primaryDimension,
    filters: effectiveFilters,
  });

  const { config } = usePageConfig();
  const revelioPageId = config.revelioPageId;
  const defaultMetricModeOverride = useDefaultPostingsMetricModeOverride();

  const pageDefaultMetricMode: 'posting' | 'expected_hires' =
    revelioPageId !== undefined &&
    POSTING_COUNT_MODE_PAGE_IDS.has(revelioPageId)
      ? 'posting'
      : 'expected_hires';
  const defaultMetricMode = defaultMetricModeOverride ?? pageDefaultMetricMode;

  const variables = usePostingsV3Variables({
    primaryDimension,
    effectiveFilters,
    isSnapshot,
    defaultMetricMode,
    activeFilterMap,
  });

  const {
    weekRefs,
    isLoading: isWeekRefsLoading,
    error: weekRefsError,
  } = useWeekRefs();
  if (!isPrimaryFilterSet) return null;

  const dates = variables.req.filters_v2 ?? variables.req.filters;
  if (!dates?.start_date || !dates?.end_date) {
    return null;
  }

  // Without the week id -> date map every timeseries point id misses the lookup
  // and is dropped, rendering an empty chart — surface it as an error instead.
  if (weekRefsError) {
    return children({ data: undefined, status: DataProviderQueryStatus.Error });
  }

  return (
    <RequestedPostingsModeProvider value={variables.req.options.mode}>
      <PostingsV3Query variables={variables} pause={isWeekRefsLoading}>
        {({ data, status }) =>
          children({
            data: convertPostingsV3Data({
              data,
              metric,
              weekRefs,
              secondaryDimension,
              exchangeRate,
            }),
            status,
          })
        }
      </PostingsV3Query>
    </RequestedPostingsModeProvider>
  );
};

const PostingsV3Query = ({
  variables,
  pause = false,
  children,
}: {
  variables: PostingsV3DataQueryVariables;
  pause?: boolean;
  children: DataProviderChildren<PostingsV3DataQuery | undefined>;
}) => {
  const isTaxonomyResolved = useIsTaxonomyResolved();
  const [{ data, status }] = useUrqlQueryWithStatus<PostingsV3DataQuery>({
    query: POSTINGS_V3_GET_DATA,
    variables,
    pause: !isTaxonomyResolved || pause,
  });

  return children({ data, status });
};

export type PostingsV3RawResult = {
  data: PostingsV3DataQuery | undefined;
  weekRefs: ReadonlyMap<number, string>;
  status: DataProviderQueryStatus;
};

/**
 * Raw postings_v3 provider for MixedStats text cards. Fetches the postings_v3
 * response plus the week id -> date map and hands both back so the consumer can
 * run its own conversion (`convertPostingsV3Data`).
 */
export const PostingsV3DataProviderRaw = ({
  primaryDimension,
  defaultMetricMode = 'expected_hires',
  isSnapshot = false,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  defaultMetricMode?: 'posting' | 'expected_hires';
  isSnapshot?: boolean;
  children: (result: PostingsV3RawResult) => ReactNode;
}) => {
  const { activeFilterMap } = useFilterContext();
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'week' });

  const variables = usePostingsV3Variables({
    primaryDimension,
    effectiveFilters,
    isSnapshot,
    defaultMetricMode,
    activeFilterMap,
  });

  const {
    weekRefs,
    isLoading: isWeekRefsLoading,
    error: weekRefsError,
  } = useWeekRefs();

  return (
    <PostingsV3Query variables={variables} pause={isWeekRefsLoading}>
      {({ data, status }) =>
        children({
          data,
          weekRefs,
          status: weekRefsError ? DataProviderQueryStatus.Error : status,
        })
      }
    </PostingsV3Query>
  );
};
